import {Record, Set} from 'immutable';
import DashMetricsActions from 'actions/dashboard/DashMetricsActions';
import DashMetricsSource, {DirectionType, MetricType} from 'sources/dashboard/DashMetricsSource';
import {RepProfileTab} from '@store/coaching-plans';
import {coachingTabConfigs, callTabConfigs} from 'components/dashboard/rollups/tabConfigs';
import omit from 'lodash/omit';
import RepProfileTrendChartContainer from '$pages/@common/rep-profile-trend-chart/RepProfileTrendChartContainer';
import ExecTrendChartContainer from 'components/pure/metrics/ExecTrendChartContainer';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import ExecDashboardActions from 'actions/ExecDashboardActions';
import UserActions from 'actions/UserActions';

/**
 * Given a tab config object a set of all the types used.
 * @param tabConfigs
 * @param exclusions - list of types to exclude from the final set
 */
function metricTypes(tabConfigs, exclusions = []) {
  let metrics = Set();
  for (const [key, val] of Object.entries(tabConfigs)) {
    metrics = metrics.add(key).union(Object.keys(val.subtabs || {}));
  }
  return metrics.subtract(exclusions);
}

const repProfileTrendChart = RepProfileTrendChartContainer;
const execTrendChart = ExecTrendChartContainer;

const fetchRepTrendData = (stat, params) =>
  RepProfilePageActions.getTrendData({userId: stat.id, ...params});

const fetchExecTrendData = (stat, params) => {
  const teamId = stat.id;
  UserActions.fetchAll({teamId});
  ExecDashboardActions.getTrendData({teamId, ...params});
};

export class RollupConfig extends Record({
  className: '',
  tabConfigs: undefined,
  tabMetrics: [],
  defaultTab: undefined,
  direction: undefined,
  linkDestination: undefined,
  descriptionsFn: undefined,
  trendComponent: repProfileTrendChart,
  fetchTrendData: fetchRepTrendData,
  fetchMetricData: DashMetricsActions.userMetric,
  fetchMetricSource: DashMetricsSource.userMetric,
}) {
  constructor(obj) {
    // todo enforce some invariants here
    super({
      ...obj,
      tabConfigs: omit(obj.tabConfigs, obj.exclude),
      tabMetrics: metricTypes(obj.tabConfigs, obj.exclude),
    });
  }

  get isUserBased() {
    return this.trendComponent === repProfileTrendChart;
  }
}

export const otherCoaching = new RollupConfig({
  className: 'dash-rollup-other-coaching',
  tabConfigs: coachingTabConfigs,
  defaultTab: MetricType.listeningCount,
  direction: DirectionType.coachByOthers,
  linkDestination: RepProfileTab.COACHING_RECEIVED,
  descriptionsFn: ({manager}) => ({
    [MetricType.listeningCount]: `Total number of calls listened to by others (except ${manager.firstName})`,
    [MetricType.listeningPercent]: `Average percentage of calls listened to by others (except ${manager.firstName})`,
    [MetricType.listeningTime]: `Total time spent listening to calls by others (except ${manager.firstName})`,
    [MetricType.commentsCount]: `Number of comments left on calls listened to by others (except ${manager.firstName})`,
    [MetricType.commentsLength]: `Total number of characters in comments left on calls by others (except ${manager.firstName})`,
    [MetricType.highlightsCount]: `Number of highlights left on calls by others (except ${manager.firstName})`,
    [MetricType.callsScored]: `Number of scorecards filled in by others (except ${manager.firstName})`,
    [MetricType.libraryAdds]: `Number of calls added to libraries by others (except ${manager.firstName})`,
    [MetricType.plans]: `Number of coaching plans with someone other than ${manager.firstName} as the coach`,
    [MetricType.meetings]: `Number of coaching meetings with someone other than ${manager.firstName} as the coach`,
  }),
});

export const selfReview = new RollupConfig({
  className: 'dash-rollup-self-review',
  tabConfigs: coachingTabConfigs,
  defaultTab: MetricType.listeningCount,
  direction: DirectionType.reviewBySelf,
  linkDestination: RepProfileTab.REVIEW_ACTIVITIES,
  descriptionsFn: () => ({
    [MetricType.listeningCount]: `Per rep, total number of their own calls listened to`,
    [MetricType.listeningPercent]: `Per rep, average percentage of their own calls listened to`,
    [MetricType.listeningTime]: `Per rep, total time spent listening to their own calls`,
    [MetricType.commentsCount]: `Per rep, number of comments left on their own calls`,
    [MetricType.commentsLength]: `Per rep, the total length of all comments left on their own calls`,
    [MetricType.highlightsCount]: `Per rep, the total number of highlights left on their own calls`,
    [MetricType.libraryAdds]: `Per rep, number of their own calls added to library`,
    [MetricType.callsScored]: `Per rep, number of scorecards filled in on their own calls`,
  }),
});

export const peerCoaching = new RollupConfig({
  className: 'dash-rollup-peer-coaching',
  tabConfigs: coachingTabConfigs,
  defaultTab: MetricType.listeningCount,
  direction: DirectionType.coachByOthers,
  linkDestination: RepProfileTab.REVIEW_ACTIVITIES,
  exclude: [MetricType.meetings, MetricType.plans],
  descriptionsFn: () => ({
    [MetricType.listeningCount]: `Per rep, total number of calls by their peers listened to`,
    [MetricType.listeningPercent]: `Per rep, average percentage of calls by their peers listened to`,
    [MetricType.listeningTime]: `Per rep, total time spent listening to calls by their peers`,
    [MetricType.commentsCount]: `Per rep, number of comments left on calls by their peers`,
    [MetricType.commentsLength]: `Per rep, total number of characters in all comments left on calls by their peers`,
    [MetricType.highlightsCount]: `Per rep, total number of highlights left on calls by their peers`,
    [MetricType.libraryAdds]: `Per rep, number of calls by their peers added to libraries`,
    [MetricType.callsScored]: `Per rep, number of scorecards filled in on calls by their peers`,
  }),
});

export const managerCoaching = new RollupConfig({
  className: 'dash-rollup-manager-coaching',
  tabConfigs: coachingTabConfigs,
  defaultTab: MetricType.listeningCount,
  direction: DirectionType.coachByManager,
  linkDestination: RepProfileTab.COACHING_RECEIVED,
  descriptionsFn: ({manager}) => ({
    [MetricType.listeningCount]: `Total number of calls listened to by ${manager.firstName} `,
    [MetricType.listeningPercent]: `Average percentage of calls listened to by ${manager.firstName} `,
    [MetricType.listeningTime]: `Total time spent listening to calls by ${manager.firstName}`,
    [MetricType.commentsCount]: `Number of comments left on calls by ${manager.firstName}`,
    [MetricType.commentsLength]: `Total number of characters in comments left on calls by ${manager.firstName}`,
    [MetricType.highlightsCount]: `Total number of highlights left on calls by ${manager.firstName}`,
    [MetricType.libraryAdds]: `Number of calls added to libraries by ${manager.firstName} `,
    [MetricType.callsScored]: `Number of scorecards filled in by ${manager.firstName}`,
    [MetricType.meetings]: `Number of coaching meetings with ${manager.firstName} as the coach`,
    [MetricType.plans]: `Number of coaching plans with ${manager.firstName} as the coach`,
  }),
});

export const callActivity = new RollupConfig({
  tabConfigs: callTabConfigs,
  defaultTab: MetricType.totalDuration,
  direction: DirectionType.activity,
  linkDestination: RepProfileTab.CALL_ACTIVITY,
  descriptionsFn: () => ({
    [MetricType.totalDuration]: `Total duration of conversations`,
    [MetricType.conversations]: `Number of conversations`,
    [MetricType.avgDuration]: `Average duration of conversations`,
    [MetricType.talkListen]: `Average percentage of time the rep spent listening vs talking`,
    [MetricType.interchanges]: `Average interchanges per minute of conversation`,
  }),
});

export const coachMetrics = new RollupConfig({
  tabConfigs: coachingTabConfigs,
  defaultTab: MetricType.listeningCount,
  fetchMetricData: DashMetricsActions.coachMetric,
  fetchMetricSource: DashMetricsSource.coachMetric,
  direction: DirectionType.coachBySelf,
  linkDestination: RepProfileTab.REVIEW_ACTIVITIES,
  descriptionsFn: () => ({
    [MetricType.listeningCount]: `Total number of calls listened to by coaches`,
    [MetricType.listeningPercent]: `Average percentage of calls listened to by coaches`,
    [MetricType.listeningTime]: `Total time spent listening to calls by coaches`,
    [MetricType.commentsCount]: `Number of comments left on calls by coaches`,
    [MetricType.commentsLength]: `Total number of characters in comments left on calls by coaches`,
    [MetricType.highlightsCount]: `Total number of highlights left on calls by coaches`,
    [MetricType.libraryAdds]: `Number of calls added to libraries by coaches`,
    [MetricType.callsScored]: `Number of scorecards filled in by coaches`,
    [MetricType.meetings]: `Number of coaching meetings conducted with coaches`,
    [MetricType.plans]: `Number of coaching plans conducted by coaches`,
  }),
});

export const managerMetrics = new RollupConfig({
  tabConfigs: coachingTabConfigs,
  defaultTab: MetricType.listeningCount,
  direction: DirectionType.coachByManager,
  fetchMetricData: DashMetricsActions.teamMetric,
  fetchMetricSource: DashMetricsSource.teamMetric,
  linkDestination: 'your-coaching',
  trendComponent: execTrendChart,
  fetchTrendData: fetchExecTrendData,
  descriptionsFn: () => ({
    [MetricType.listeningCount]: `Total number of calls listened to by managers`,
    [MetricType.listeningPercent]: `Average percentage of calls listened to by managers`,
    [MetricType.listeningTime]: `Total time spent listening to calls by managers`,
    [MetricType.commentsCount]: `Number of comments left on calls by managers`,
    [MetricType.commentsLength]: `Total number of characters in comments left on calls by managers`,
    [MetricType.highlightsCount]: `Total number of highlights left on calls by managers`,
    [MetricType.libraryAdds]: `Number of calls added to libraries by managers`,
    [MetricType.callsScored]: `Number of scorecards filled in by managers`,
    [MetricType.meetings]: `Number of coaching meetings conducted with managers`,
    [MetricType.plans]: `Number of coaching plans conducted by managers`,
  }),
});

export const peerMetrics = new RollupConfig({
  tabConfigs: coachingTabConfigs,
  fetchMetricData: DashMetricsActions.teamMetric,
  fetchMetricSource: DashMetricsSource.teamMetric,
  defaultTab: MetricType.listeningCount,
  direction: DirectionType.coachByOthers,
  linkDestination: 'other-coaching',
  trendComponent: execTrendChart,
  fetchTrendData: fetchExecTrendData,
  descriptionsFn: () => ({
    [MetricType.listeningCount]: `Total number of calls listened to by others (except the manager)`,
    [MetricType.listeningPercent]: `Average percentage of calls listened to by others (except the manager)`,
    [MetricType.listeningTime]: `Total time spent listening to calls by others (except the manager)`,
    [MetricType.commentsCount]: `Number of comments left on calls by others (except the manager)`,
    [MetricType.commentsLength]: `Total number of characters in comments left on calls by others (except the manager)`,
    [MetricType.highlightsCount]: `Total number of highlights left on calls by others (except the manager)`,
    [MetricType.libraryAdds]: `Number of calls added to libraries by others (except the manager)`,
    [MetricType.callsScored]: `Number of scorecards filled in by others (except the manager)`,
    [MetricType.meetings]: `Number of coaching meetings with someone other than the manager as the coach`,
    [MetricType.plans]: `Number of coaching plans with someone other than the manager as the coach`,
  }),
});

export const adminCallActivity = new RollupConfig({
  tabConfigs: callTabConfigs,
  fetchMetricData: DashMetricsActions.teamMetric,
  fetchMetricSource: DashMetricsSource.teamMetric,
  defaultTab: MetricType.totalDuration,
  direction: DirectionType.activity,
  linkDestination: 'rep-activity',
  trendComponent: execTrendChart,
  fetchTrendData: fetchExecTrendData,
  descriptionsFn: () => ({
    [MetricType.totalDuration]: `Total duration of conversations`,
    [MetricType.conversations]: `Number of conversations`,
    [MetricType.avgDuration]: `Average duration of conversations`,
    [MetricType.talkListen]: `Average percentage of time the rep spent listening vs talking`,
    [MetricType.interchanges]: `Average interchanges per minute of conversation`,
  }),
});
